import { useCallback, useTransition, useState } from "react";
import { findOffer, type FindOfferResponseBody } from "services/onlineApplicationService";
import { useAppDispatch, useAppSelector } from "store/hooks";
import {
    saveOfferData,
    resetApplication,
    updateOfferAvailability,
    resetApplicationKeepOfferData,
} from "store/application/applicationSlice";
import { assertDefined } from "utils/assertions";
import { emptyToUndefined, map, toLowerCaseGenderFromSingleLetter } from "utils/conversions";
import { UNKNOWN_AAA_MEMBER_NUMBER } from "constants/validations";
import { pushGTMEvent } from "utils/gtmEvent";
import { GA_EVENT_CONFIGS } from "constants/gaEvents";
import type { OfferData } from "types/OfferData";
import { ROUTES } from "routes/constants";

export type OfferProcessingResult = {
    success: boolean;
    route?: string;
    offer?: FindOfferResponseBody;
};

export const useOffer = () => {
    const reduxDispatch = useAppDispatch();
    const [isPending, startTransition] = useTransition();
    const [error, setError] = useState<string | null>(null);

    const offerData = useAppSelector((state) => state.application.offerData);

    const handleOffer = useCallback(
        (offer: FindOfferResponseBody): OfferProcessingResult => {
            const aaaMemberNumber =
                offer.aaaMemberNumber && offer.aaaMemberNumber !== UNKNOWN_AAA_MEMBER_NUMBER
                    ? offer.aaaMemberNumber
                    : undefined;

            const offerData: OfferData = {
                invitationCode: assertDefined(offer.finderNumber, "Finder number is required"),
                firstName: map(offer.firstName, emptyToUndefined),
                lastName: map(offer.lastName, emptyToUndefined),
                planCode: assertDefined(offer.planCode, "Plan code is required"),
                offer: (offer.offer ?? "").split("|"),
                keyCode: assertDefined(offer.keycode, "Key code is required"),
                campaignCode: assertDefined(offer.campaignCode, "Campaign code is required"),
                aaaMemberNumber: aaaMemberNumber,
                memberOfferAvailable: offer.memberOfferAvailable,
                spouseOfferAvailable: offer.spouseOfferAvailable,
                membershipLength: offer.memberLoyaltyYears,
                memberSince: map(offer.memberJoinDate ?? undefined, emptyToUndefined),
                gender: toLowerCaseGenderFromSingleLetter(offer.gender) ?? undefined,
                address: offer.address,
                state: map(offer.state, emptyToUndefined),
                city: map(offer.city, emptyToUndefined),
                zipCode: map(offer.zipCd, emptyToUndefined),
            };

            reduxDispatch(saveOfferData(offerData));

            pushGTMEvent(GA_EVENT_CONFIGS.INVITATION_SUBMIT_SUCCESS);

            return { success: true, route: ROUTES.QUOTE, offer };
        },
        [reduxDispatch]
    );

    const validateAndProcessOffer = useCallback(
        (invitationCodeOrPurl: string, isPurl = false): Promise<OfferProcessingResult> => {
            // Clear Redux state before processing new invitation code.
            // Preserve sessionId — it was established on page mount and must persist across the flow.
            reduxDispatch(resetApplication({ preserveSessionId: true }));
            setError(null);

            return new Promise<OfferProcessingResult>((resolve) => {
                startTransition(async () => {
                    try {
                        const findOfferRequestBody = isPurl
                            ? { purl: invitationCodeOrPurl }
                            : { finderNumber: invitationCodeOrPurl };
                        const offer = await findOffer(findOfferRequestBody);

                        if (!offer.memberOfferAvailable && !offer.spouseOfferAvailable) {
                            resolve({ success: true, route: ROUTES.WELCOME_BACK, offer });
                            return;
                        }

                        resolve(handleOffer(offer));
                    } catch (error) {
                        console.error("Failed to validate or process invitation code:", error);
                        // Track API rejection
                        if (isPurl) {
                            // PURL auto-fill failed
                            pushGTMEvent(GA_EVENT_CONFIGS.INVITATION_PURL_ERROR);
                        } else {
                            // Manual invitation code entry
                            setError("Invitation code is invalid");
                            pushGTMEvent(GA_EVENT_CONFIGS.INVITATION_API_ERROR);
                        }
                        resolve({ success: false });
                    }
                });
            });
        },
        [reduxDispatch, handleOffer, startTransition]
    );

    const setMemberOfferAvailable = useCallback(
        (available: boolean) => {
            reduxDispatch(updateOfferAvailability({ memberOfferAvailable: available }));
        },
        [reduxDispatch]
    );

    const setSpouseOfferAvailable = useCallback(
        (available: boolean) => {
            reduxDispatch(updateOfferAvailability({ spouseOfferAvailable: available }));
        },
        [reduxDispatch]
    );

    const resetApplicationKeepOffer = useCallback(
        (options?: { preserveSessionId: boolean }) => {
            reduxDispatch(resetApplicationKeepOfferData(options));
        },
        [reduxDispatch]
    );

    return {
        offerData,
        validateAndProcessOffer,
        isLoading: isPending,
        error,
        setError,
        setMemberOfferAvailable,
        setSpouseOfferAvailable,
        resetApplicationKeepOffer,
    };
};
